import { Box, Divider, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Header from '/@/components/bayer/header';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import { useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getSessionBrands } from '/@/store/session/selectors';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import Section from '/@/views/recommendations/v2/section';
import CardCarousel from '/@/views/recommendations/v2/steps/management/card-carousel';
import DensityCaption from '/@/views/recommendations/v2/steps/management/density-caption';
import DensityCurve from '/@/views/recommendations/v2/steps/management/density-curve';
import DensityEnvironmentTable from '/@/views/recommendations/v2/steps/management/density-environment-table';
import DensitySimulation from '/@/views/recommendations/v2/steps/management/density-simulation';
import { DEFAULT_ENVIRONMENT_VARIABILITY } from '/@/views/recommendations/v2/steps/management/density-simulation-data';
import { NutrientTable } from '/@/views/recommendations/v2/steps/management/fertilization-tab';
import HybridDensityCard from '/@/views/recommendations/v2/steps/management/hybrid-density-card';
import PhenologyTimeline, { PhenologyLegend } from '/@/views/recommendations/v2/steps/management/phenology-timeline';
import { useManagementData } from '/@/views/recommendations/v2/steps/management/use-management-data';
import { usePhenology } from '/@/views/recommendations/v2/steps/management/use-phenology';
import GeneralData from './general-data';
import { getResultTitle, isSingleHybridLayout } from './presentation';
import { useSavedRecommendation } from './use-saved-recommendation';

/**
 * Read-only view of a saved seed recommendation — where "Terminar" lands.
 *
 * Every section is the component the wizard's last step renders, driven off the same
 * `activeRecommendation` slice, so the two can't drift. Nothing here is editable.
 */
function RecommendationResultV2({
    recommendationId,
    crop = 'corn',
}: {
    recommendationId: number;
    crop?: 'corn' | 'soy';
}) {
    const { recommendation, parsedRecommendation, field, cropConfig, isReady, variant } = useSavedRecommendation({
        recommendationId,
        crop,
    });
    const { data: grower } = useGetUserNode(recommendation?.userNode as number);
    // A recommendation on the user's own fields has no grower to name — the node it hangs off
    // is theirs. Naming them back to themselves reads as noise, so the card drops out.
    const { data: ownUserNode } = useOwnUserNode();
    const isOwnNode = !!grower?.id && grower.id === ownUserNode?.id;
    // The case the legacy seed detail uses, so both pages show one breadcrumb trail.
    const breadcrumbs = useBreadcrumbs('recommendation.RecommendationDetail');
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendation?.commentsStatus,
        recommendation?.userNode
    );

    const { hybrids, planterEfficiency, areaInHectares, isLoading } = useManagementData({ field, crop });
    const { hybrids: phenologyHybrids } = usePhenology({ field, crop, plantingDate: cropConfig?.plantingDate });
    const sessionBrands = useSelector(getSessionBrands);
    // `showDensityChart` is per brand and they disagree (Dekalb enables it, Preceon doesn't),
    // so the table is gated per hybrid rather than on whichever brand comes first: a list
    // spanning both brands would otherwise show Preceon a table its own brand turns off.
    const densityChartBrandIds = useMemo(
        () => new Set((sessionBrands?.[crop] || []).filter((brand) => brand.showDensityChart).map((brand) => brand.id)),
        [sessionBrands, crop]
    );
    const densityTableHybrids = useMemo(
        () => hybrids.filter((hybrid) => densityChartBrandIds.has(hybrid.brandId)),
        [hybrids, densityChartBrandIds]
    );
    const showsDensityTable = densityTableHybrids.some((hybrid) => hybrid.densityTableValues.length > 0);

    const [firstHybrid] = hybrids;
    const singleHybrid = isSingleHybridLayout(hybrids.length) && !!firstHybrid;
    const title = getResultTitle({ variant, hybridName: firstHybrid?.catalogName, cropName: getCrops()[crop]?.name });

    const lastUpdated = recommendation?.modified || recommendation?.created;

    const commentsMenuItem = {
        label: _('Ver o dejar comentarios'),
        onClick: () => setCommentsDrawerOpen(true),
        hidden: !(recommendation?.id && shouldShowCommentsButton),
    };

    // The phone's nav bar carries a short title; the full one and the last-updated line sit in
    // the content, above the field card.
    const mobileHeader = (
        <MobileHeader
            title={_('Recomendación')}
            breadcrumbs={breadcrumbs}
            moreButton={
                parsedRecommendation && (
                    <RecommendationShare
                        type={crop}
                        recommendation={parsedRecommendation}
                        version={2}
                        pdfVersion={2}
                        entrySource='recommendation-detail'
                        variant='menu'
                        extraMenuItems={[commentsMenuItem]}
                    />
                )
            }
        />
    );

    const header = (
        <Header
            variant='full-gray'
            breadcrumbs={breadcrumbs}
            title={title}
            subtitle={
                lastUpdated
                    ? _('Última actualización {{date}}', { date: dayjs(lastUpdated).format('DD/MM/YYYY') })
                    : undefined
            }
            actions={[
                // `disabled` covers the loading window; the prop itself isn't optional.
                parsedRecommendation && (
                    <RecommendationShare
                        key='recommendation-share'
                        type={crop}
                        recommendation={parsedRecommendation}
                        version={2}
                        pdfVersion={2}
                        entrySource='recommendation-detail'
                    />
                ),
                recommendation?.id && shouldShowCommentsButton && (
                    <RecommendationCommentsButton
                        key='comments-button'
                        commentsStatus={recommendation.commentsStatus}
                        onOpenDrawer={() => setCommentsDrawerOpen(true)}
                    />
                ),
            ].filter(Boolean)}
        />
    );

    if (!isReady) {
        return (
            <PageContainer
                heroHeader={isSmOrLower ? undefined : header}
                header={isSmOrLower ? mobileHeader : undefined}
            >
                <Stack alignItems='center' justifyContent='center' py={10}>
                    <Spinner />
                </Stack>
            </PageContainer>
        );
    }

    return (
        <PageContainer heroHeader={isSmOrLower ? undefined : header} header={isSmOrLower ? mobileHeader : undefined}>
            {/* `MobileHeader` bleeds past the container with a negative margin, whose bottom half
                eats the padding below it — so the phone needs double to clear the nav bar. */}
            <Stack spacing={isSmOrLower ? 3 : 4} pt={isSmOrLower ? 4 : 2} pb={8}>
                {isSmOrLower && (
                    <Stack spacing={1}>
                        <Typography variant='h6' fontWeight='bold' color='primary'>
                            {title}
                        </Typography>
                        {lastUpdated && (
                            <Typography variant='caption' color='primary.mui.inactive'>
                                {_('Última actualización {{date}}', {
                                    date: dayjs(lastUpdated).format('DD/MM/YYYY'),
                                })}
                            </Typography>
                        )}
                    </Stack>
                )}
                <GeneralData
                    grower={grower}
                    hideGrower={isOwnNode}
                    field={field}
                    plantingDate={cropConfig?.plantingDate}
                    environment={cropConfig?.environment}
                    climatePattern={cropConfig?.climatePattern}
                    soilMoisture={cropConfig?.soilMoisture}
                    crop={crop}
                    compact={isSmOrLower}
                />

                {isLoading && !hybrids.length ? (
                    <Stack alignItems='center' py={6}>
                        <Spinner />
                    </Stack>
                ) : singleHybrid ? (
                    /* One hybrid has nothing to compare against, so the card moves up beside the
                       curve instead of sitting in a section of its own above it. */
                    <Stack spacing={2}>
                        <Box
                            display='grid'
                            gap={4}
                            gridTemplateColumns={{ xs: '1fr', md: 'minmax(280px, 1fr) 2fr' }}
                            alignItems='start'
                        >
                            <Section
                                title={_('Híbrido')}
                                subtitle={_('Rendimiento estimado según las condiciones del lote.')}
                            >
                                <HybridDensityCard
                                    hybrid={firstHybrid}
                                    planterEfficiency={planterEfficiency}
                                    areaInHectares={areaInHectares}
                                />
                            </Section>

                            <Section
                                title={_('Densidad objetivo')}
                                subtitle={_(
                                    'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                                )}
                                block='density_curve'
                            >
                                <DensityCurve hybrids={hybrids} />
                            </Section>
                        </Box>
                        <DensityCaption planterEfficiency={planterEfficiency} areaInHectares={areaInHectares} />
                    </Stack>
                ) : (
                    <>
                        <Section
                            title={_('Híbridos recomendados')}
                            subtitle={_('Híbridos recomendados según el ambiente y las condiciones del lote.')}
                        >
                            {/* Too wide to stack on a phone, so they swipe there and grid on desktop. */}
                            {isSmOrLower ? (
                                <CardCarousel>
                                    {hybrids.map((hybrid) => (
                                        <HybridDensityCard
                                            key={hybrid.id}
                                            hybrid={hybrid}
                                            planterEfficiency={planterEfficiency}
                                            areaInHectares={areaInHectares}
                                        />
                                    ))}
                                </CardCarousel>
                            ) : (
                                <Box
                                    display='grid'
                                    gap={2}
                                    gridTemplateColumns={{ sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                                    alignItems='stretch'
                                >
                                    {hybrids.map((hybrid) => (
                                        <HybridDensityCard
                                            key={hybrid.id}
                                            hybrid={hybrid}
                                            planterEfficiency={planterEfficiency}
                                            areaInHectares={areaInHectares}
                                        />
                                    ))}
                                </Box>
                            )}
                            <DensityCaption planterEfficiency={planterEfficiency} areaInHectares={areaInHectares} />
                        </Section>

                        <Divider />

                        <Section
                            title={_('Densidad objetivo')}
                            subtitle={_(
                                'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                            )}
                            block='density_curve'
                        >
                            <DensityCurve hybrids={hybrids} />
                        </Section>
                    </>
                )}

                {!isLoading && cropConfig?.userDensity ? (
                    <>
                        <Divider />

                        {/* The same section the wizard shows, read-only: the card carries its own title. */}
                        <DensitySimulation
                            readOnly
                            hybrids={hybrids}
                            environment={cropConfig.environment}
                            userDensity={cropConfig.userDensity}
                            variability={cropConfig.userBetterEnvironment ?? DEFAULT_ENVIRONMENT_VARIABILITY}
                            worseVariability={
                                cropConfig.userWorseEnvironment ??
                                cropConfig.userBetterEnvironment ??
                                DEFAULT_ENVIRONMENT_VARIABILITY
                            }
                        />
                    </>
                ) : null}

                {showsDensityTable && (
                    <>
                        <Divider />

                        <Section
                            title={_('Densidad según el ambiente')}
                            subtitle={_('Densidad de siembra recomendada para cada rango de ambiente.')}
                            block='density_table'
                        >
                            <DensityEnvironmentTable hybrids={densityTableHybrids} />
                        </Section>
                    </>
                )}

                <Divider />

                <Section
                    title={_('Fenología según la fecha de siembra')}
                    subtitle={_('Fechas de cada estadio y período crítico según la fecha de siembra.')}
                    block='phenology'
                >
                    <PhenologyTimeline hybrids={phenologyHybrids} />
                    <PhenologyLegend />
                </Section>

                <Divider />

                <Section
                    title={_('Requerimiento nutricional')}
                    subtitle={_(
                        'Nutrientes totales que necesitan los híbridos seleccionados para el rendimiento estimado.'
                    )}
                    block='nutrients'
                >
                    <NutrientTable hybrids={hybrids} />
                </Section>
            </Stack>

            {recommendation?.id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={recommendation.id}
                />
            )}
        </PageContainer>
    );
}

export default RecommendationResultV2;
